import type { AppState, MarketCategory, MarketIntentPayment, MarketRarityFilter, MarketSort } from '../state/store';
import type {
  Cosmetic,
  CosmeticListing,
  InventoryItem,
  ListingCurrency,
  MarketListing,
  MarketListingItem,
} from '../api/types';
import { affixSummary, durabilityText, rarityLabel, slotLabel } from '../inventory/format';
import { formatRawTokenAmount } from './format';
import { btn, esc, pill } from './components';
import { iconRow, itemIcon } from './icons';
import { copyCounts, kindPill, skinPreview } from './cosmeticsView';

/**
 * Marketplace screen (Phase G + cosmetics consolidation).
 *
 * Browsing is category-first: Weapon / Armor / Trinkets / Lantern gear
 * categories with a rarity filter and price/newest sort, plus a Cosmetics
 * category hosting the player-to-player cosmetic listings (with skin filter
 * and sort). The Cosmetics page keeps claims + shop; trading lives here.
 * List panels only show entries the player can actually list.
 */

export const CATEGORIES: Array<{ key: MarketCategory; label: string }> = [
  { key: 'weapon', label: 'Weapon' },
  { key: 'armor', label: 'Armor' },
  { key: 'trinket', label: 'Trinkets' },
  { key: 'lantern', label: 'Lantern' },
  { key: 'cosmetics', label: 'Cosmetics' },
];

const RARITY_OPTIONS: Array<{ value: MarketRarityFilter; label: string }> = [
  { value: 'all', label: 'All rarities' },
  { value: 'common', label: 'Common' },
  { value: 'uncommon', label: 'Uncommon' },
  { value: 'rare', label: 'Rare' },
  { value: 'epic', label: 'Epic' },
  { value: 'relic', label: 'Relic' },
];

// ── filtering / sorting (exported for tests) ─────────────────────────────────

export function filterGearListings(
  listings: readonly MarketListing[],
  category: MarketCategory,
  rarity: MarketRarityFilter,
): MarketListing[] {
  if (category === 'cosmetics') return [];
  return listings.filter((l) => l.item.slotType === category && (rarity === 'all' || l.item.rarity === rarity));
}

/**
 * Deterministic sort. newest → listedAt desc across BOTH currencies. price →
 * Gold and $OBOL amounts are not comparable, so listings group by currency
 * (Gold first, then $OBOL) and sort ascending WITHIN each currency by that
 * currency's own amount. Ties fall back to newest, then id.
 */
export function sortGearListings(listings: readonly MarketListing[], sort: MarketSort): MarketListing[] {
  const listedMs = (l: MarketListing) => Date.parse(l.listedAt) || 0;
  const amount = (l: MarketListing) => (l.currency === 'gold' ? l.price : (l.obolAmountTotal ?? 0));
  return [...listings].sort((a, b) => {
    let cmp = 0;
    if (sort === 'price') {
      cmp = (a.currency === b.currency ? 0 : a.currency === 'gold' ? -1 : 1);
      if (cmp === 0) cmp = amount(a) - amount(b);
    }
    if (cmp === 0) cmp = listedMs(b) - listedMs(a);
    if (cmp === 0) cmp = a.id.localeCompare(b.id);
    return cmp;
  });
}

export function filterCosmeticListings(listings: readonly CosmeticListing[], skin: string): CosmeticListing[] {
  if (skin === 'all') return [...listings];
  return listings.filter((l) => l.cosmeticId === skin);
}

export interface CurrencyFilters { gold: boolean; obol: boolean }

/** Keep only listings in the currencies the buyer has checked. */
export function filterByCurrency<T extends { currency: ListingCurrency }>(
  listings: readonly T[],
  currencies: CurrencyFilters,
): T[] {
  return listings.filter((l) => currencies[l.currency]);
}

/** Same currency-grouped price semantics as {@link sortGearListings}. */
export function sortCosmeticListings(listings: readonly CosmeticListing[], sort: MarketSort): CosmeticListing[] {
  const listedMs = (l: CosmeticListing) => Date.parse(l.listedAt) || 0;
  const amount = (l: CosmeticListing) => (l.currency === 'gold' ? l.price : (l.obolAmountTotal ?? 0));
  return [...listings].sort((a, b) => {
    let cmp = 0;
    if (sort === 'price') {
      cmp = (a.currency === b.currency ? 0 : a.currency === 'gold' ? -1 : 1);
      if (cmp === 0) cmp = amount(a) - amount(b);
    }
    if (cmp === 0) cmp = listedMs(b) - listedMs(a);
    if (cmp === 0) cmp = a.id.localeCompare(b.id);
    return cmp;
  });
}

/** Gear the player can list right now (sealed, non-bound, unequipped, not already listed). */
export function listableGearItems(state: Pick<AppState, 'inventory' | 'myListings'>): InventoryItem[] {
  const activeItemIds = new Set((state.myListings ?? []).filter((l) => l.status === 'listed').map((l) => l.itemInstanceId));
  return (state.inventory ?? []).filter((it) =>
    !it.item.accountBound &&
    it.item.sealState === 'sealed' &&
    !it.equippedSlot &&
    !activeItemIds.has(it.itemInstanceId));
}

/** Cosmetics with at least one listable spare copy (tradeable, non-bound, unreserved). */
export function listableCosmetics(state: Pick<AppState, 'myCosmetics'>): Cosmetic[] {
  return (state.myCosmetics ?? []).filter((c) =>
    c.tradeable && !c.accountBound && copyCounts(c).listableCount > 0);
}

// ── shared bits ──────────────────────────────────────────────────────────────

function itemMeta(item: MarketListingItem | InventoryItem['item']): string {
  return `${esc(affixSummary(item.affixes))} · dura ${esc(durabilityText(item.durabilityCur, item.durabilityMax))} · seal ${esc(item.sealDepth)}`;
}

function rarityTitle(item: MarketListingItem | InventoryItem['item']): string {
  return `<span class="rarity rarity--${esc(item.rarity)}">${esc(rarityLabel(item.rarity))} ${esc(slotLabel(item.slotType))}</span>`;
}

function obolDecimals(state: AppState): number | null {
  return state.cashoutTokenDecimals ?? state.marketWalletBalance?.decimals ?? null;
}

function obolText(amountRaw: number | null, state: AppState): string {
  return `${formatRawTokenAmount(amountRaw === null ? null : BigInt(amountRaw), obolDecimals(state))} $OBOL`;
}

function pricePill(l: { currency: ListingCurrency; price: number; obolAmountTotal: number | null }, state: AppState): string {
  return l.currency === 'gold'
    ? `<span class="pill pill--ok">${esc(l.price)} Gold</span>`
    : `<span class="pill pill--warn">${esc(obolText(l.obolAmountTotal, state))}</span>`;
}

/** Per-category currency visibility toggles (both on by default). */
function currencyToggles(state: AppState): string {
  const c = state.marketCurrencies;
  const box = (key: 'gold' | 'obol', label: string, checked: boolean) => `
    <label class="market-currency-toggle">
      <input type="checkbox" data-market-currency="${key}"${checked ? ' checked' : ''} aria-label="Show ${esc(label)} listings" />
      <span>${esc(label)}</span>
    </label>`;
  return `<span class="market-currency-toggles">${box('gold', 'Gold listings', c.gold)}${box('obol', '$OBOL listings', c.obol)}</span>`;
}

function isReservedByOther(l: { reservedUntil: string | null }, state: AppState): boolean {
  if (!l.reservedUntil || Date.parse(l.reservedUntil) <= Date.now()) return false;
  // A reservation held by the caller (their own active intent) stays payable.
  return !(state.myMarketIntents ?? []).some((i) => i.status === 'active' && i.expiresAt === l.reservedUntil);
}

/** The caller's own active intent on this listing that already has a wallet-sent signature. */
function sentPaymentForListing(listingId: string, state: AppState): { intentId: string } | null {
  const intent = (state.myMarketIntents ?? []).find((i) => i.status === 'active' && i.listingId === listingId);
  if (!intent || !state.marketIntentPayments[intent.id]) return null;
  return { intentId: intent.id };
}

/**
 * Action row for an $OBOL listing: "Pay with wallet", failing closed BEFORE
 * Phantom on insufficient/unverifiable balance and while another buyer's
 * intent reserves the listing. Fee copy stays compact (95/5 split).
 */
function obolListingAction(
  l: { id: string; obolAmountTotal: number | null; reservedUntil: string | null },
  kind: 'gear' | 'cosmetic',
  state: AppState,
): string {
  const feeCopy = '<span class="muted market-fee-copy">Seller receives 95%, treasury 5%</span>';
  if (isReservedByOther(l, state)) {
    return `${pill('reserved by another buyer', 'warn')} ${feeCopy}`;
  }
  // A payment was already sent for this reservation: never reopen the wallet
  // from the listing card — settlement continues from Pending $OBOL purchases.
  if (sentPaymentForListing(l.id, state)) {
    return `${pill('payment sent — see Pending $OBOL purchases above', 'warn')} ${feeCopy}`;
  }
  const balance = state.marketWalletBalance;
  const payLabel = `Pay with wallet · ${obolText(l.obolAmountTotal, state)}`;
  if (state.marketWalletBalanceError) {
    return `${pill('$OBOL balance unavailable — try refresh', 'warn')} ${btn(payLabel, 'market-pay-obol', { listing: l.id, kind }, { disabled: true })} ${feeCopy}`;
  }
  if (balance && l.obolAmountTotal !== null && BigInt(balance.amountRaw) < BigInt(l.obolAmountTotal)) {
    return `${pill('Insufficient $OBOL for this listing.', 'warn')} ${btn(payLabel, 'market-pay-obol', { listing: l.id, kind }, { disabled: true })} ${feeCopy}`;
  }
  return `${btn(payLabel, 'market-pay-obol', { listing: l.id, kind }, { variant: 'primary' })} ${feeCopy}`;
}

function headerCard(state: AppState): string {
  const gold = state.goldBalance ?? 0;
  return `
    <section class="card card--wide market-head">
      <div class="run__head">
        <h2><span class="ember-dot" aria-hidden="true"></span> Marketplace</h2>
        <div class="btn-row">
          ${btn('Refresh', 'market-refresh', {}, { variant: 'ghost' })}
          ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
        </div>
      </div>
      <p class="muted">Trade sealed, non-bound gear and spare cosmetic copies with other players for Gold. Account-bound entries cannot be listed or traded. <strong>0% fee</strong> — the seller keeps 100%.</p>
      ${iconRow('Your Gold', 'gold', `<strong>${esc(gold)}</strong>`)}
    </section>`;
}

function categoryTabs(current: MarketCategory): string {
  const tabs = CATEGORIES
    .map(({ key, label }) => `
      <button type="button" class="btn ${key === current ? 'btn--primary' : 'btn--ghost'} market-category-tab" data-act="market-category" data-category="${esc(key)}" aria-pressed="${key === current ? 'true' : 'false'}">${esc(label)}</button>`)
    .join('');
  return `<nav class="market-categories" aria-label="Marketplace categories">${tabs}</nav>`;
}

function sortSelect(current: MarketSort): string {
  return `
    <label class="cashout-sort">Sort
      <select data-market-sort aria-label="Sort listings">
        <option value="newest"${current === 'newest' ? ' selected' : ''}>Newest</option>
        <option value="price"${current === 'price' ? ' selected' : ''}>Price</option>
      </select>
    </label>`;
}

// ── gear categories ──────────────────────────────────────────────────────────

function raritySelect(current: MarketRarityFilter): string {
  return `
    <label class="cashout-sort">Rarity
      <select data-market-rarity aria-label="Filter by rarity">
        ${RARITY_OPTIONS.map(({ value, label }) => `<option value="${esc(value)}"${value === current ? ' selected' : ''}>${esc(label)}</option>`).join('')}
      </select>
    </label>`;
}

function gearListingCard(l: MarketListing, state: AppState): string {
  const isOwn = l.sellerAccountId === state.account?.account.id;
  let action: string;
  if (isOwn) {
    action = pill('your listing — cannot buy', 'muted');
  } else if (l.currency === 'obol') {
    action = obolListingAction(l, 'gear', state);
  } else if ((state.goldBalance ?? 0) < l.price) {
    action = `${pill('not enough Gold', 'warn')} ${btn(`Buy · ${l.price} Gold`, 'market-buy', { listing: l.id }, { disabled: true })}`;
  } else {
    action = btn(`Buy · ${l.price} Gold`, 'market-buy', { listing: l.id }, { variant: 'primary' });
  }
  return `
    <div class="item">
      <div class="item__head">${itemIcon(l.item.slotType)}${rarityTitle(l.item)} ${pricePill(l, state)}</div>
      <div class="item__meta">seller <code>${esc(l.sellerLabel)}</code></div>
      <div class="item__meta">${itemMeta(l.item)}</div>
      <div class="btn-row">${action}</div>
    </div>`;
}

function gearListingsCard(state: AppState, categoryLabel: string): string {
  const noCurrency = !state.marketCurrencies.gold && !state.marketCurrencies.obol;
  const listings = sortGearListings(
    filterByCurrency(
      filterGearListings(state.marketListings ?? [], state.marketCategory, state.marketRarity),
      state.marketCurrencies,
    ),
    state.marketSort,
  );
  const body = noCurrency
    ? '<p class="muted">No listing currencies selected.</p>'
    : listings.length > 0
      ? `<div class="items">${listings.map((l) => gearListingCard(l, state)).join('')}</div>`
      : `<p class="muted">No active ${esc(categoryLabel.toLowerCase())} listings match these filters.</p>`;
  return `
    <section class="card card--wide">
      <div class="run__head">
        <h2>${esc(categoryLabel)} listings</h2>
        <div class="btn-row">${currencyToggles(state)}${raritySelect(state.marketRarity)}${sortSelect(state.marketSort)}</div>
      </div>
      ${body}
    </section>`;
}

/**
 * Currency selector + both price inputs for a listing form row. `prefix` is
 * '' for gear (data-currency-for/data-price-for/data-obol-price-for) and
 * 'cosmetic-' for cosmetics — matching the main.ts input lookups.
 */
function listingCurrencyInputs(prefix: '' | 'cosmetic-', id: string): string {
  return `
          <select class="market-currency-select" data-${prefix}currency-for="${esc(id)}" aria-label="Listing currency">
            <option value="gold" selected>Gold</option>
            <option value="obol">$OBOL</option>
          </select>
          <input class="market-price" type="number" min="1" step="1" data-${prefix}price-for="${esc(id)}" placeholder="Gold price" aria-label="Gold price" />
          <input class="market-price" type="text" inputmode="decimal" data-${prefix}obol-price-for="${esc(id)}" placeholder="Total $OBOL price" aria-label="Total $OBOL price" />`;
}

const OBOL_LISTING_HINT =
  '$OBOL listings use wallet payment. Seller receives 95%; treasury receives 5%. Gold listings keep the 0% fee.';

/** List-an-item panel: eligible gear only — no unsealed/equipped/bound/listed clutter. */
function listGearForm(state: AppState): string {
  const items = listableGearItems(state);
  const cards = items
    .map((it) => `
      <div class="item">
        <div class="item__head">${itemIcon(it.item.slotType)}${rarityTitle(it.item)}</div>
        <div class="item__meta">${esc(affixSummary(it.item.affixes))} · dura ${esc(durabilityText(it.item.durabilityCur, it.item.durabilityMax))} · <code>${esc(it.itemInstanceId.slice(0, 8))}</code></div>
        <div class="btn-row">
          ${listingCurrencyInputs('', it.itemInstanceId)}
          ${btn('List', 'market-list', { item: it.itemInstanceId }, { variant: 'primary' })}
        </div>
      </div>`)
    .join('');
  const body = items.length > 0 ? `<div class="items">${cards}</div>` : '<p class="muted">No listable gear available.</p>';
  return `
    <section class="card card--wide">
      <h2>List an item</h2>
      <p class="muted">Only sealed, non-account-bound, unequipped gear can be listed. Listing locks the item until sold or cancelled. ${esc(OBOL_LISTING_HINT)}</p>
      ${body}
    </section>`;
}

function myGearListingsCard(state: AppState): string {
  const listings = state.myListings ?? [];
  const cards = listings
    .map((l) => {
      const statusPill =
        l.status === 'listed' ? pill('listed', 'ok') : l.status === 'sold' ? pill('sold', 'muted') : pill('cancelled', 'down');
      let action = '';
      if (l.status === 'listed') {
        action = btn('Cancel listing', 'market-cancel', { listing: l.id }, { variant: 'ghost' });
      } else if (l.status === 'sold' && l.buyerLabel) {
        action = `<span class="muted">sold to <code>${esc(l.buyerLabel)}</code></span>`;
      }
      return `
        <div class="item">
          <div class="item__head">${itemIcon(l.item.slotType)}${rarityTitle(l.item)} ${statusPill}</div>
          <div class="item__meta">price <strong>${l.currency === 'gold' ? `${esc(l.price)} Gold` : esc(obolText(l.obolAmountTotal, state))}</strong> · ${itemMeta(l.item)}</div>
          <div class="btn-row">${action}</div>
        </div>`;
    })
    .join('');
  const body = listings.length > 0 ? `<div class="items">${cards}</div>` : '<p class="muted">You have no listings yet.</p>';
  return `
    <section class="card card--wide">
      <h2>My listings</h2>
      ${body}
    </section>`;
}

// ── cosmetics category ───────────────────────────────────────────────────────

/** Skin filter options: every distinct skin among active listings, by title. */
export function skinFilterOptions(listings: readonly CosmeticListing[]): Array<{ value: string; label: string }> {
  const byId = new Map<string, string>();
  for (const l of listings) byId.set(l.cosmeticId, l.cosmetic.title);
  const options = [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [{ value: 'all', label: 'All skins' }, ...options];
}

function skinSelect(listings: readonly CosmeticListing[], current: string): string {
  const options = skinFilterOptions(listings);
  const selected = options.some((o) => o.value === current) ? current : 'all';
  return `
    <label class="cashout-sort">Skin
      <select data-market-skin aria-label="Filter by skin">
        ${options.map(({ value, label }) => `<option value="${esc(value)}"${value === selected ? ' selected' : ''}>${esc(label)}</option>`).join('')}
      </select>
    </label>`;
}

function cosmeticListingCard(l: CosmeticListing, state: AppState): string {
  const isOwn = l.sellerAccountId === state.account?.account.id;
  const ownedCopy = (state.myCosmetics ?? []).find((c) => c.id === l.cosmeticId);
  const ownedNote = ownedCopy ? `${pill(`Owned ×${copyCounts(ownedCopy).quantity}`, 'muted')} ` : '';
  let action: string;
  if (isOwn) {
    action = pill('your listing — cannot buy', 'muted');
  } else if (l.currency === 'obol') {
    action = `${ownedNote}${obolListingAction(l, 'cosmetic', state)}`;
  } else if ((state.cosmeticsGoldBalance ?? state.goldBalance ?? 0) < l.price) {
    action = `${pill('not enough Gold', 'warn')} ${btn(`Buy · ${l.price} Gold`, 'cosmetic-market-buy', { listing: l.id }, { disabled: true })}`;
  } else {
    // Multi-copy ownership: buying a copy you already own adds another copy.
    action = `${ownedNote}${btn(`Buy${ownedCopy ? ' another' : ''} · ${l.price} Gold`, 'cosmetic-market-buy', { listing: l.id }, { variant: 'primary' })}`;
  }
  return `
    <div class="item cosmetic-item shop-card">
      ${skinPreview(l.cosmetic)}
      <div class="item__head">
        <span class="rarity rarity--common">${esc(l.cosmetic.title)}</span>
        ${kindPill(l.cosmetic.kind)}
        ${pricePill(l, state)}
      </div>
      <div class="item__meta">seller <code>${esc(l.sellerLabel)}</code></div>
      <div class="btn-row">${action}</div>
    </div>`;
}

function cosmeticListingsCard(state: AppState): string {
  const all = state.cosmeticListings ?? [];
  const noCurrency = !state.marketCurrencies.gold && !state.marketCurrencies.obol;
  const listings = sortCosmeticListings(
    filterByCurrency(filterCosmeticListings(all, state.marketSkin), state.marketCurrencies),
    state.marketSort,
  );
  const body = noCurrency
    ? '<p class="muted">No listing currencies selected.</p>'
    : listings.length > 0
      ? `<div class="items">${listings.map((l) => cosmeticListingCard(l, state)).join('')}</div>`
      : '<p class="muted">No active cosmetic listings match these filters.</p>';
  return `
    <section class="card card--wide">
      <div class="run__head">
        <h2>Cosmetic listings</h2>
        <div class="btn-row">${currencyToggles(state)}${skinSelect(all, state.marketSkin)}${sortSelect(state.marketSort)}</div>
      </div>
      <p class="muted">Player-to-player cosmetic trading. Spare copies only: the equipped last copy stays yours, listed copies are reserved, and bound cosmetics cannot be traded.</p>
      ${body}
    </section>`;
}

/** List-a-cosmetic panel: listable spare copies only. */
function listCosmeticsForm(state: AppState): string {
  const cosmetics = listableCosmetics(state);
  const cards = cosmetics
    .map((c) => {
      const { listableCount } = copyCounts(c);
      return `
        <div class="item cosmetic-item shop-card">
          ${skinPreview(c)}
          <div class="item__head">
            <span class="rarity rarity--common">${esc(c.title)}</span>
            ${kindPill(c.kind)}
            ${pill(`${listableCount} spare cop${listableCount === 1 ? 'y' : 'ies'} listable`, 'muted')}
          </div>
          <div class="btn-row">
            ${listingCurrencyInputs('cosmetic-', c.id)}
            ${btn(listableCount > 1 ? `List 1 of ${listableCount} spare` : 'List spare copy', 'cosmetic-market-list', { cosmetic: c.id }, { variant: 'primary' })}
          </div>
        </div>`;
    })
    .join('');
  const body = cosmetics.length > 0 ? `<div class="items">${cards}</div>` : '<p class="muted">No listable cosmetics available.</p>';
  return `
    <section class="card card--wide">
      <h2>List a cosmetic</h2>
      <p class="muted">Listing reserves one spare copy until sold or cancelled. Your equipped copy is never listed. ${esc(OBOL_LISTING_HINT)}</p>
      ${body}
    </section>`;
}

/** Own cosmetic listings worth showing: active ones and sale history — a cancelled listing is dead weight. */
export function visibleMyCosmeticListings(listings: readonly CosmeticListing[]): CosmeticListing[] {
  return listings.filter((l) => l.status !== 'cancelled');
}

function myCosmeticListingsCard(state: AppState): string {
  const listings = visibleMyCosmeticListings(state.myCosmeticListings ?? []);
  const cards = listings.map((l) => {
    const statusPill = l.status === 'listed' ? pill('listed', 'ok') : l.status === 'sold' ? pill('sold', 'muted') : pill('cancelled', 'down');
    const action = l.status === 'listed'
      ? btn('Cancel listing', 'cosmetic-market-cancel', { listing: l.id }, { variant: 'ghost' })
      : l.status === 'sold' && l.buyerLabel
        ? `<span class="muted">sold to <code>${esc(l.buyerLabel)}</code></span>`
        : '';
    return `
      <div class="item cosmetic-item shop-card">
        ${skinPreview(l.cosmetic)}
        <div class="item__head">
          <span class="rarity rarity--common">${esc(l.cosmetic.title)}</span>
          ${kindPill(l.cosmetic.kind)}
          ${statusPill}
        </div>
        <div class="item__meta">${l.currency === 'gold' ? `${esc(l.price)} Gold` : esc(obolText(l.obolAmountTotal, state))}</div>
        <div class="btn-row">${action}</div>
      </div>`;
  }).join('');
  const body = listings.length > 0 ? `<div class="items">${cards}</div>` : '<p class="muted">You have no cosmetic listings yet.</p>';
  return `
    <section class="card card--wide">
      <h2>My cosmetic listings</h2>
      ${body}
    </section>`;
}

/**
 * Buyer's still-payable $OBOL purchase intents. Reload-safe: after a wallet
 * hiccup the buyer can paste the signature here; expired intents disappear on
 * refresh (backend returns active+unexpired only).
 */
/** Status + actions for an intent whose wallet payment was already sent. */
function sentPaymentRow(intentId: string, payment: MarketIntentPayment): string {
  const statusPill = payment.status === 'confirming'
    ? pill('Payment sent — waiting for confirmation…', 'warn')
    : pill('Payment sent but not confirmed yet', 'warn');
  const retry = btn('Retry settlement', 'market-intent-retry', { intent: intentId }, { variant: 'primary', disabled: payment.status === 'confirming' });
  return `
        <div class="item__meta">${statusPill} signature <code>${esc(payment.signature.slice(0, 8))}…</code></div>
        <div class="btn-row">
          ${retry}
          <input class="market-price" type="text" data-market-tx-for="${esc(intentId)}" placeholder="Paste tx signature (fallback)" aria-label="Transaction signature" />
          ${btn('Submit signature', 'market-intent-submit', { intent: intentId }, { variant: 'ghost' })}
        </div>`;
}

function pendingObolPurchasesCard(state: AppState): string {
  const intents = (state.myMarketIntents ?? []).filter(
    (i) => i.status === 'active' && Date.parse(i.expiresAt) > Date.now(),
  );
  const payments = state.marketIntentPayments;
  // Sent payments whose reservation is no longer in the active list (expired
  // before confirmation / restored after a refresh): still recoverable — the
  // server settles a late submit while the listing remains reserved.
  const orphans = Object.entries(payments).filter(([intentId]) => !intents.some((i) => i.id === intentId));
  if (intents.length === 0 && orphans.length === 0) return '';
  const cards = intents.map((i) => {
    const payment = payments[i.id];
    const mock = i.paymentInstructions?.devMockSignature
      ? btn('Submit dev mock payment', 'market-intent-submit-mock', { intent: i.id, tx: i.paymentInstructions.devMockSignature }, { variant: 'ghost' })
      : '';
    // Once a signature exists for this reservation the wallet must NOT be
    // reopened — settlement retries reuse the stored signature.
    const actions = payment
      ? sentPaymentRow(i.id, payment)
      : `
        <div class="btn-row">
          ${btn(`Pay with wallet · ${obolText(i.obolAmountTotal, state)}`, 'market-pay-obol', { listing: i.listingId, kind: i.listingKind }, { variant: 'primary' })}
          <input class="market-price" type="text" data-market-tx-for="${esc(i.id)}" placeholder="Paste tx signature" aria-label="Transaction signature" />
          ${btn('Submit signature', 'market-intent-submit', { intent: i.id }, { variant: 'ghost' })}
          ${mock}
        </div>`;
    return `
      <div class="item">
        <div class="item__head">
          <span>${i.listingKind === 'gear' ? 'Gear' : 'Cosmetic'} purchase</span>
          ${pill(obolText(i.obolAmountTotal, state), 'warn')}
          <span class="muted market-fee-copy">95/5 split</span>
        </div>
        <div class="item__meta">Reserved for you until <span title="${esc(i.expiresAt)}">${esc(new Date(i.expiresAt).toLocaleTimeString())}</span> — pay before it expires.</div>
        ${actions}
      </div>`;
  }).join('');
  const orphanCards = orphans.map(([intentId, payment]) => `
      <div class="item">
        <div class="item__head">
          <span>${payment.listingKind === 'gear' ? 'Gear' : 'Cosmetic'} purchase — unconfirmed payment</span>
        </div>
        ${sentPaymentRow(intentId, payment)}
        <div class="btn-row">${btn('Discard stored signature', 'market-intent-dismiss', { intent: intentId }, { variant: 'ghost' })}</div>
      </div>`).join('');
  return `
    <section class="card card--wide">
      <h2>Pending $OBOL purchases</h2>
      <p class="muted">Your reserved listings awaiting wallet payment. The server verifies the on-chain payment before the item or cosmetic transfers.</p>
      <div class="items">${cards}${orphanCards}</div>
    </section>`;
}

// ── entry point ──────────────────────────────────────────────────────────────

export function renderMarketplace(state: AppState): string {
  if (!state.session) {
    return `<section class="card"><h2>Marketplace</h2><p class="muted">Connect a wallet to trade items for Gold.</p></section>`;
  }
  const category = state.marketCategory;
  const categoryLabel = CATEGORIES.find((c) => c.key === category)?.label ?? 'Gear';
  const body = category === 'cosmetics'
    ? `${pendingObolPurchasesCard(state)}${cosmeticListingsCard(state)}${listCosmeticsForm(state)}${myCosmeticListingsCard(state)}`
    : `${pendingObolPurchasesCard(state)}${gearListingsCard(state, categoryLabel)}${listGearForm(state)}${myGearListingsCard(state)}`;
  return `
    <div class="grid">
      ${headerCard(state)}
      <section class="card card--wide market-categories-card">${categoryTabs(category)}</section>
      ${body}
    </div>`;
}
